import { getLocation, RouterLocation } from "connected-react-router";
import { matchPath } from "react-router-dom";
import { select } from "redux-saga/effects";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import PinLegend, {
  ColorPinLegend,
  ShapePinLegend,
} from "@mapmycustomers/shared/types/map/PinLegend";
import User from "@mapmycustomers/shared/types/User";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import { getUserMetaData } from "@app/store/iam";
import { getMapViewSettings } from "@app/store/map";
import resolveLegendKeys from "@app/store/map/utils/resolveLegendKeys";
import ROUTE_DETAILS_PATHS from "@app/store/map/utils/routeDetailsPaths";
import {
  getAllColorLegends,
  getAllShapeLegends,
  getDefaultColorKeys,
  getDefaultShapeKeys,
} from "@app/store/pinLegends";
import buildPinLegends from "@app/util/map/buildPinLegends";

type DefaultKeys = Partial<Record<EntityTypesSupportedByMapsPage, PinLegend["id"]>>;

// Builds the pinLegends request filter from the legend of the surface currently on screen. The
// route-details pages keep their own selection and paint their pins from it, so a pane or picker
// open beside one must resolve colours from the same selection rather than the map's.
function* getPinLegendsFilter(entityTypes: EntityTypesSupportedByMapsPage[]) {
  const colorPinLegends: ColorPinLegend[] = yield select(getAllColorLegends);
  const shapePinLegends: ShapePinLegend[] = yield select(getAllShapeLegends);

  const { pathname }: RouterLocation<unknown> = yield select(getLocation);
  const isOnRouteDetails = Object.values(ROUTE_DETAILS_PATHS).some((path) =>
    matchPath(pathname, { path })
  );

  let legendKeys: Pick<MapViewState, "colorKey" | "shapeKey">;
  if (isOnRouteDetails) {
    const userMetaData: User["metaData"] = yield select(getUserMetaData);
    const defaultColorKeys: DefaultKeys = yield select(getDefaultColorKeys);
    const defaultShapeKeys: DefaultKeys = yield select(getDefaultShapeKeys);
    legendKeys = resolveLegendKeys(
      userMetaData.routeViewSettings,
      colorPinLegends,
      shapePinLegends,
      defaultColorKeys,
      defaultShapeKeys
    );
  } else {
    // getMapViewSettings guarantees a colorKey and shapeKey per entity type, but reads user
    // metadata, so it trails a key change by one updateMe round trip.
    const mapViewState: MapViewState = yield select(getMapViewSettings);
    legendKeys = mapViewState;
  }

  return buildPinLegends(entityTypes, legendKeys, colorPinLegends, shapePinLegends);
}

export default getPinLegendsFilter;
